// ═══════════════════════════════════════════════════════════════════════════
// FILE: src/app/components/message-popup/message-popup.component.ts
// DATE: 2026-08-09
// PURPOSE: Das Popup, das eine eintreffende Nachricht zeigt — mit den vier
//          CFLX-Aussagen, dem Sachbezug, zwei UTC-Zeitstempeln und einem
//          Klick, der zur Ursache führt.
// ceeq.it: Item #78 in _34 · Task #310
//
// DIE KLICKFLÄCHE
//   Die ganze Karte führt zur Stelle, an der die Nachricht entstanden ist —
//   meist ein Task oder eine Diskussion. Ausgenommen sind genau zwei Dinge:
//   das Schliessen-Kreuz oben rechts und die Schaltfläche unten. Beide halten
//   den Klick an (stopPropagation), damit Wegklicken nie versehentlich
//   navigiert.
//
// ZWEI ZEITSTEMPEL
//   Ereignis    wann der Vorgang war (aus der Nachricht)
//   Angezeigt   wann diese Karte vor Augen kam (beim Anzeigen gesetzt)
//   Beide in UTC und beide sichtbar. In einem verteilten Team mit Agenten,
//   die rund um die Uhr arbeiten, ist die Ortszeit des Betrachters die
//   unbrauchbarere Angabe von beiden.
//
// FARBE IST NIE DAS EINZIGE SIGNAL
//   Die Verbindlichkeit steht zusätzlich als Wort da (MUSS / PRÜFEN / INFO)
//   und als unterschiedlich breiter Streifen. Farbe allein schliesst
//   farbfehlsichtige Nutzer aus.
//
// VERWANDT: services/message-popup.service.ts (Reihenfolge und Warteschlange)
//           types/ceeq-message.ts (der Vertrag)
// ═══════════════════════════════════════════════════════════════════════════

import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CeeqMessage, OBLIGATION_LABEL, Obligation } from '../../types/ceeq-message';
import { MessagePopupService } from '../../services/message-popup.service';

/** Ein Abschnitt der Karte: Beschriftung und Text. */
interface Aussage {
  schluessel: string;
  wert: string;
  art: 'status' | 'frage' | 'wiederholung' | 'ich' | 'appell';
}

@Component({
  selector: 'app-message-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './message-popup.component.html',
  styleUrls: ['./message-popup.component.scss'],
})
export class MessagePopupComponent {
  private popup = inject(MessagePopupService);
  private router = inject(Router);

  readonly eintrag = this.popup.aktuell;
  readonly wartend = this.popup.wartend;

  /** Die vier CFLX-Aussagen, in Leserichtung und ohne die leeren. */
  readonly aussagen = computed<Aussage[]>(() => {
    const m = this.eintrag()?.nachricht;
    if (!m) return [];
    const c = m.cflx || { status_stmnt: null, imessage: null, appeal: null, qrepeat: null };
    const raus: Aussage[] = [];

    if (c.status_stmnt) {
      // Ein Sachstand kann auch eine Frage sein. Dann heisst er so.
      const frage = c.status_stmnt.trim().endsWith('?');
      raus.push({
        schluessel: frage ? 'Frage' : 'Sachstand',
        wert: c.status_stmnt,
        art: frage ? 'frage' : 'status',
      });
    }
    if (c.qrepeat) raus.push({ schluessel: 'Ich frage erneut', wert: c.qrepeat, art: 'wiederholung' });
    if (c.imessage) raus.push({ schluessel: 'Ich-Botschaft', wert: c.imessage, art: 'ich' });
    if (c.appeal) raus.push({ schluessel: 'Appell', wert: c.appeal, art: 'appell' });

    return raus;
  });

  readonly stufe = computed<Obligation>(
    () => (this.eintrag()?.nachricht.obligation ?? 'fyi_logged') as Obligation,
  );

  readonly stufeWort = computed(() => OBLIGATION_LABEL[this.stufe()] ?? 'INFO');

  /** Klartext für den Anlass — der technische Schlüssel hilft niemandem. */
  readonly anlass = computed(() => {
    const t = this.eintrag()?.nachricht.event_type ?? '';
    const worte: Record<string, string> = {
      'post.appeal': 'Beitrag mit Appell',
      'post.plain': 'Neuer Beitrag',
      'task.created': 'Task angelegt',
      'task.status_changed': 'Task-Status gewechselt',
      'item.created': 'Item angelegt',
      'item.status_changed': 'Item-Status gewechselt',
    };
    return worte[t] || t || 'Vorgang';
  });

  // Anker getrennt bereitgestellt. Der Typ sagt, dass `anchor` immer da ist;
  // der Umschlag aus dem Testpfad hält sich daran nicht zwingend. Die Prüfung
  // gehört deshalb hierher und nicht in die Vorlage — dort wäre sie laut
  // Übersetzer überflüssig (NG8107) und der Build klagt bei jedem Lauf.
  readonly ankerTyp = computed(() => this.eintrag()?.nachricht?.anchor?.type ?? null);
  readonly ankerId  = computed(() => this.eintrag()?.nachricht?.anchor?.id ?? null);

  /** Wohin der Klick führt — beschriftet, damit man es vorher weiss. */
  readonly zielWort = computed(() => {
    const m = this.eintrag()?.nachricht;
    const worte: Record<string, string> = {
      task: 'Task öffnen',
      item: 'Item öffnen',
      sprint: 'Sprint öffnen',
      milestone: 'Milestone öffnen',
      dsm_cell: 'Daily Scrum öffnen',
      discussion_post: 'Diskussion öffnen',
    };
    const wort = worte[m?.anchor?.type ?? ''];
    if (wort) return wort;

    // Ein Umschlag ohne Anker und ohne Projekt kommt aus dem Testpfad
    // (/api/ws-test/send-to-user). Dann führt der Klick zur Projektauswahl —
    // und die Beschriftung sagt das, statt „Zum Projekt" zu versprechen.
    return m?.pslot ? 'Zum Projekt' : 'Zur Projektauswahl';
  });

  // ── Zeit ─────────────────────────────────────────────────────────────────

  /** `2026-08-09 03:21:45 UTC` — bewusst ohne Ortszeit-Umrechnung. */
  utc(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    const p = (n: number, l = 2) => String(n).padStart(l, '0');
    return (
      `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ` +
      `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} UTC`
    );
  }

  /** „vor 3 Min" — beantwortet die Frage, die nach dem Zeitstempel kommt. */
  alter(iso: string | null | undefined): string {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms) || ms < 0) return '';
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'gerade eben';
    if (min < 60) return `vor ${min} Min`;
    const std = Math.floor(min / 60);
    if (std < 24) return `vor ${std} Std`;
    return `vor ${Math.floor(std / 24)} Tagen`;
  }

  // ── Handlungen ───────────────────────────────────────────────────────────

  /** Klick auf die Karte: zur Ursache springen und schliessen. */
  oeffnen(): void {
    const m = this.eintrag()?.nachricht;
    if (!m) return;

    const ziel = this.zielRoute(m);
    console.log(`[MessagePopup] öffne id=${m.id} → ${ziel.join('/')}`);
    this.popup.schliessen();
    this.router.navigate(ziel);
  }

  /** Kreuz und Schaltfläche: nur schliessen, niemals navigieren. */
  schliessen(ev: Event): void {
    ev.stopPropagation();
    this.popup.schliessen();
  }

  /** Alles Wartende verwerfen — es bleibt im Posteingang liegen. */
  alleVerwerfen(ev: Event): void {
    ev.stopPropagation();
    this.popup.alleVerwerfen();
  }

  /**
   * Der Anker sagt, woran die Nachricht hängt. Von dort führt genau ein Weg
   * zur Stelle, an der auch die Diskussion liegt.
   *
   *   task     → Task-Editor  (trägt den Diskussionsstrang)
   *   item     → Item-Ansicht (trägt den Diskussionsstrang)
   *   sprint   → Sprint-Editor
   *   sonst    → Projekt-Dashboard, damit der Klick immer irgendwo landet
   */
  private zielRoute(m: CeeqMessage): string[] {
    const p = m.pslot ?? '';
    const id = m.anchor?.id;

    if (!p) return ['/project-select'];
    if (id == null) return ['/project', p, 'dashboard'];

    switch (m.anchor?.type) {
      case 'task':      return ['/project', p, 'task', String(id), 'edit'];
      case 'item':      return ['/project', p, 'item', String(id)];
      case 'sprint':    return ['/project', p, 'sprint', String(id), 'edit'];
      case 'milestone': return ['/project', p, 'milestone', String(id), 'edit'];
      case 'dsm_cell':  return ['/project', p, 'daily-scrum-matrix'];
      default:          return ['/project', p, 'dashboard'];
    }
  }
}
